/* eslint-disable functional/immutable-data */

import React, { useCallback, useEffect } from 'react';
import type { ModalProps as RNModalProps } from 'react-native';
import { Modal as RNModal } from 'react-native';

import type { BaseModalProps, PaletteProps } from './Modal.types';
import { useResponsive } from '../../hooks';
import * as ModalSubComponents from './components';
import { useModalMachineIntegration } from './hooks';
import { useModalContext } from './Modal.provider';

export const Modal = (props: ModalProps) => {
  const {
    id,
    visible,
    palette,
    children,
    animationType,
    offset,
    size = 'small',
    fitHeight = false,
    withSafeAreaView,
    onRequestClose,
    onShowing,
    onShown,
    onDismissing,
    onDismissed,
    ...modalProps
  } = props;

  const modalContext = useModalContext();

  // ─── Animation Type ──────────────────────────────────────────────────

  const { match } = useResponsive();
  const conditionalAnimationType = animationType ?? match(['slide', 'fade']);
  const shouldRenderBackdrop = !modalProps.transparent;

  // ─── Modal Machine ───────────────────────────────────────────────────

  const { modalServiceHelpers } = useModalMachineIntegration({
    visible,
    animationType,
    onShowing,
    onShown,
    onDismissing,
    onDismissed,
  });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onModalShow = useCallback(() => {
    modalServiceHelpers.sendShowEvent();
    modalContext?.setActiveModal({ id });
  }, [id, modalContext, modalServiceHelpers]);

  const onModalDismiss = useCallback(() => {
    modalServiceHelpers.sendDismissEvent();
    modalContext?.resetActiveModal();
  }, [modalContext, modalServiceHelpers]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    return modalContext?.resetActiveModal;
  }, [modalContext?.resetActiveModal]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <RNModal
      statusBarTranslucent
      {...modalProps}
      visible={visible}
      transparent
      onRequestClose={onRequestClose}
      animationType={conditionalAnimationType}
      onShow={onModalShow}
      onDismiss={onModalDismiss}
    >
      <ModalSubComponents.ModalWrapper>
        <ModalSubComponents.ModalContainer
          size={size}
          fitHeight={fitHeight}
          offset={offset}
        >
          <ModalSubComponents.ModalContent
            palette={palette}
            shouldFitToFill={size === 'full'}
            withSafeAreaView={withSafeAreaView}
          >
            {children}
          </ModalSubComponents.ModalContent>
        </ModalSubComponents.ModalContainer>

        {shouldRenderBackdrop ? (
          <ModalSubComponents.ModalBackdrop onPress={onRequestClose} />
        ) : null}
      </ModalSubComponents.ModalWrapper>
    </RNModal>
  );
};

// ─── Types ─────────────────────────────────────────────────────────────

type ModalProps = BaseModalProps & {
  /**
   * Modals can have an optional `id`, which can be used to identify
   * them amongst other modals and perform conditional actions depending on that.
   */
  readonly id?: string;

  /**
   * Accessibility label for the modal itself.
   * Without this, the modal will show up unannounced.
   */
  readonly accessibilityLabel?: string;

  /**
   * Accessibility label for the backdrop pressable.
   */
  readonly backdropAccessibilityLabel?: string;

  /**
   * Determines whether modal content should be wrapped in the `SafeArea` container.
   */
  readonly withSafeAreaView?: boolean;

  /**
   * Fires immediately when the {visibility} is changed to {true}.
   */
  readonly onShowing?: () => void;

  /**
   * Fires when the "show" animation is finished and the modal is fully shown.
   */
  readonly onShown?: () => void;

  /**
   * Fires immediately when the {visibility} is changed to {false}.
   */
  readonly onDismissing?: () => void;

  /**
   * Fires when the "dismiss" animation is finished and the modal is fully hidden.
   */
  readonly onDismissed?: () => void;
} & Omit<RNModalProps, 'onShow' | 'onDismiss'> &
  PaletteProps;

// ───────────────────────────────────────────────────────────────────────

Modal.Row = ModalSubComponents.ModalRow;
Modal.CloseBtn = ModalSubComponents.ModalCloseBtn;
Modal.Header = ModalSubComponents.ModalHeader;
Modal.Headline = ModalSubComponents.ModalHeadline;
Modal.Subhead = ModalSubComponents.ModalSubhead;
Modal.FloatingCloseBtn = ModalSubComponents.ModalFloatingCloseBtn;
Modal.BodyText = ModalSubComponents.ModalBodyText;
Modal.Image = ModalSubComponents.ModalImage;
Modal.Footer = ModalSubComponents.ModalFooter;
Modal.WithTelemetry = Modal; // To be overwritten by clients.
